// src/utils/request.js
import axios from 'axios'
import { Message } from 'element-ui' // 假设使用element-ui的提示组件，可替换

// 创建axios实例
const service = axios.create({
  baseURL: 'https://iase-certifications.com.hk/', // 基础URL
  timeout: 10000, // 超时时间
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

// 存储正在请求的接口，用于防重复请求
const pendingRequests = new Map()

/**
 * 生成请求唯一标识
 * @param {Object} config 请求配置
 */
const generateReqKey = (config) => {
  const { method, url, params, data } = config
  // 拼接请求方法、URL、参数作为唯一标识
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&')
}

/**
 * 添加请求到pending队列
 * @param {Object} config 请求配置
 */
const addPendingRequest = (config) => {
  const requestKey = generateReqKey(config)
  config.cancelToken = config.cancelToken || new axios.CancelToken(cancel => {
    if (!pendingRequests.has(requestKey)) {
      pendingRequests.set(requestKey, cancel)
    }
  })
}

/**
 * 移除pending队列中的请求
 * @param {Object} config 请求配置
 */
const removePendingRequest = (config) => {
  const requestKey = generateReqKey(config)
  if (pendingRequests.has(requestKey)) {
    // 取消请求
    const cancel = pendingRequests.get(requestKey)
    cancel(`重复请求被取消: ${config.url}`)
    // 从队列中移除
    pendingRequests.delete(requestKey)
  }
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 防重复请求：在请求发送前检查并取消重复请求
    removePendingRequest(config)
    addPendingRequest(config)

    // 添加token（根据实际需求处理）
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => {
    // 请求错误处理
    console.error('请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 响应成功后移除pending请求
    removePendingRequest(response.config)

    const res = response.data

    // 根据后端约定的状态码处理
    if (res.code !== 1) { // 假设200为成功状态码
      Message.error({
        message: res.message || '操作失败',
        duration: 3000
      })

      // 处理特殊状态码（如登录过期）
      if (res.code === 401) {
        // 清除token并跳转登录页
        localStorage.removeItem('token')
        window.location.href = '/login'
      }

      return Promise.reject(res)
    } else {
      return res
    }
  },
  error => {
    // 响应错误处理
    console.error('响应错误:', error)
    
    // 清除当前请求的pending状态
    if (axios.isCancel(error)) {
      console.log('已取消重复请求:', error.message)
      return Promise.reject(new Error('已取消重复请求'))
    }

    // 网络错误处理
    let errorMsg = '网络异常，请稍后重试'
    if (error.response) {
      switch (error.response.status) {
        case 404:
          errorMsg = '请求地址不存在'
          break
        case 500:
          errorMsg = '服务器内部错误'
          break
        default:
          errorMsg = `请求错误: ${error.response.status}`
      }
    }

    Message.error({
      message: errorMsg,
      duration: 3000
    })

    return Promise.reject(error)
  }
)

/**
 * 通用请求方法
 * @param {String} url 请求地址
 * @param {String} method 请求方法
 * @param {Object} data 请求数据（post/put等）
 * @param {Object} params 请求参数（get等）
 * @param {Object} config 额外配置
 */
const request = (url, method = 'get', data = {}, params = {}, config = {}) => {
  return service({
    url,
    method,
    data,
    params,
    ...config
  })
}

// 封装常用请求方法
const api = {
  get: (url, params = {}, config = {}) => {
    return request(url, 'get', {}, params, config)
  },
  post: (url, data = {}, config = {}) => {
    return request(url, 'post', data, {}, config)
  },
  put: (url, data = {}, config = {}) => {
    return request(url, 'put', data, {}, config)
  },
  delete: (url, params = {}, config = {}) => {
    return request(url, 'delete', {}, params, config)
  }
}

export default api